/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { sanitize } from 'dompurify';
import { userServiceApiPath } from '../settings';
import { httpRequest } from '../shared/http';

const baseUrlUser = `${userServiceApiPath}/users`;
const baseUrlOrganization = `${userServiceApiPath}/organizations`;
const invitationsUrl = `${userServiceApiPath}/invitations`;

export default {
  create: async (organization, options) => {
    const { data: resp } = await httpRequest(`${baseUrlOrganization}`, {
      withDefaultPath: false,
      method: 'POST',
      data: organization,
      ...options,
    })();
    return sanitize(resp);
  },
  editOrganization: async (orgId, payload, options) => {
    const { data: resp } = await httpRequest(`${baseUrlOrganization}/${orgId}`, {
      withDefaultPath: false,
      method: 'PUT',
      data: payload,
      ...options,
    })();
    return sanitize(resp);
  },
  removeOrganization: async (orgId, validateOnly, options) => {
    const { data: resp } = await httpRequest(`${baseUrlOrganization}/${orgId}`, {
      method: 'DELETE',
      withDefaultPath: false,
      params: {
        validateOnly,
      },
      ...options,
    })();
    return resp;
  },
  editUserRoles: async (orgId, userId, payload, options) => {
    const { data: resp } = await httpRequest(`${baseUrlOrganization}/${orgId}/roles/${userId}`, {
      withDefaultPath: false,
      method: 'PUT',
      data: payload,
      ...options,
    })();
    return sanitize(resp);
  },

  editUser: async (userId, payload, options) => {
    const { data: resp } = await httpRequest(`${baseUrlUser}/${userId}`, {
      withDefaultPath: false,
      method: 'PUT',
      data: payload,
      ...options,
    })();
    return sanitize(resp);
  },
  removeUser: async (userId, validateOnly, options) => {
    const { data: resp } = await httpRequest(`${baseUrlUser}/${userId}`, {
      method: 'DELETE',
      withDefaultPath: false,
      params: {
        validateOnly,
      },
      ...options,
    })();
    return resp;
  },
  getOrganizationByName: async (orgName, options) => {
    const { data: resp } = await httpRequest(`${baseUrlOrganization}`, {
      withDefaultPath: false,
      method: 'GET',
      params: {
        name: orgName,
      },
      ...options,
    })();
    return resp;
  },
  getOrganizationById: async (orgId, options) => {
    const { data: resp } = await httpRequest(`${baseUrlOrganization}/${orgId}`, {
      withDefaultPath: false,
      method: 'GET',
      ...options,
    })();
    return resp;
  },
  getUsers: async (organizationId, pagingSortingAndFiltering, options) => {
    const { data: resp } = await httpRequest(`${userServiceApiPath}/organizations/${organizationId}/members`, {
      withDefaultPath: false,
      method: 'POST',
      params: {
        page: pagingSortingAndFiltering.pageNumber,
        size: pagingSortingAndFiltering.numberOfElementsOnAPage,
        sort:
          pagingSortingAndFiltering.sortColumn && pagingSortingAndFiltering.sortOrder
            ? `${pagingSortingAndFiltering.sortColumn},${pagingSortingAndFiltering.sortOrder}`
            : undefined,
      },
      data: { filter: pagingSortingAndFiltering.filter },
      ...options,
    })();
    return resp;
  },
  validateEmail: async (email, options) => {
    const { data: resp } = await httpRequest(`${userServiceApiPath}/users/email/${email}`, {
      withDefaultPath: false,
      method: 'GET',
      ...options,
    })();
    return resp;
  },
  validateUsername: async (username, options) => {
    const { data: resp } = await httpRequest(`${userServiceApiPath}/users/username/${username}`, {
      withDefaultPath: false,
      method: 'GET',
      ...options,
    })();
    return resp;
  },
  getUsersBySearch: async (searchString, otherParams, options) => {
    const { data: resp } = await httpRequest(`${userServiceApiPath}/users`, {
      withDefaultPath: false,
      method: 'GET',
      params: {
        searchString,
        ...otherParams,
      },
      ...options,
    })();
    return resp;
  },
  getUser: async (userId, options) => {
    const { data: resp } = await httpRequest(`${baseUrlUser}/${userId}`, {
      method: 'GET',
      withDefaultPath: false,
      ...options,
    })();
    return resp;
  },
  removeUserFromOrganization: async (userId, orgId, options) => {
    const { data: resp } = await httpRequest(`${baseUrlOrganization}/${orgId}/members/${userId}`, {
      method: 'DELETE',
      withDefaultPath: false,
      ...options,
    })();
    return resp;
  },
  removeUsersFromOrganization: async (orgId, users, options) => {
    const { data: resp } = await httpRequest(`${baseUrlOrganization}/${orgId}/members`, {
      method: 'DELETE',
      withDefaultPath: false,
      data: users,
      ...options,
    })();
    return resp;
  },
  getInvitations: async (organizationId, pagingSortingAndFiltering, options) => {
    const { data: resp } = await httpRequest(`${userServiceApiPath}/organizations/${organizationId}/invitations`, {
      withDefaultPath: false,
      method: 'POST',
      params: {
        page: pagingSortingAndFiltering.pageNumber,
        size: pagingSortingAndFiltering.numberOfElementsOnAPage,
        sort:
          pagingSortingAndFiltering.sortColumn && pagingSortingAndFiltering.sortOrder
            ? `${pagingSortingAndFiltering.sortColumn},${pagingSortingAndFiltering.sortOrder}`
            : undefined,
      },
      data: { filter: pagingSortingAndFiltering.filter },
      ...options,
    })();
    return resp;
  },
  acceptInvitation: async (invitationId, options) => {
    const { data: resp } = await httpRequest(`${invitationsUrl}/${invitationId}`, {
      method: 'PUT',
      withDefaultPath: false,
      ...options,
    })();
    return resp;
  },
  declineInvitation: async (invitationId, options) => {
    const { data: resp } = await httpRequest(`${invitationsUrl}/${invitationId}`, {
      method: 'DELETE',
      withDefaultPath: false,
      ...options,
    })();
    return resp;
  },
  sendInvitations: async (orgId, invitations, options) => {
    const { data: resp } = await httpRequest(`${baseUrlOrganization}/${orgId}/invite`, {
      withDefaultPath: false,
      method: 'POST',
      data: invitations,
      ...options,
    })();
    return sanitize(resp);
  },
  removeInvitationFromOrganization: async (orgId, invitationId, options) => {
    const { data: resp } = await httpRequest(`${baseUrlOrganization}/${orgId}/invitations/${invitationId}`, {
      method: 'DELETE',
      withDefaultPath: false,
      ...options,
    })();
    return resp;
  },
  resendInvitation: async (orgId, invitationId, options) => {
    const { data: resp } = await httpRequest(`${baseUrlOrganization}/${orgId}/invitations/${invitationId}`, {
      withDefaultPath: false,
      method: 'PUT',
      ...options,
    })();
    return sanitize(resp);
  },
  switchOrganization: async (organizationId, options) => {
    const { data: resp } = await httpRequest(`${baseUrlUser}/currentOrganization/${organizationId}`, {
      method: 'PATCH',
      withDefaultPath: false,
      ...options,
    })();
    return sanitize(resp);
  },
  mergeUserAccounts: async (userToMergeId, currentUserId, options) => {
    const { data: resp } = await httpRequest(`${userServiceApiPath}/users/${userToMergeId}/merge/${currentUserId}`, {
      withDefaultPath: false,
      method: 'POST',
      data: null,
      ...options,
    })();
    return sanitize(resp);
  },
};
